import { Button, Dropdown, Popover, Space, Tooltip } from "antd";
import "./AppHeader.scss";
import Logo from "./Logo";
import CartIcon from "./icons/CartIcon";
import { useRecoilState, useSetRecoilState } from "recoil";
import { memberState, menuChatState } from "../state/atoms";
import UserBadge from "./UserBadge";
import TemplatesMenu from "./TemplatesMenu";
import SolutionsMenu from "./SolutionsMenu";
import SearchCategories from "./SearchCategories";
import { NavLink, useLocation } from "react-router-dom";
import { env } from "../env/env";
import GearIcon from "./icons/GearIcon";
import { LocationDto, MemberDto } from "../generated/dashboard-client";
import React, { useContext, useEffect, useRef, useState } from "react";
import {isDSAPath, isLandingPath} from "../util/utils";
import MobileAppHeader from "./mobile/MobileAppHeader";
import OrdersApi from "../api/OrdersApi";
import { AppContext } from "../AppContext";
import {logout} from "../service/AuthService";
import { ArrowRightOutlined, MessageOutlined } from "@ant-design/icons";
import { hasFeature, MENU_CHAT } from "../util/features";
import { isProSubscription } from "../service/BillingService";
import ProductMenu from "./ProductMenu";
import { HELP_CENTER_URL } from "../util/constants";
import UseCasesMenu from "./UseCasesMenu";
import ResourcesMenu from "./ResourcesMenu";

export interface AppHeaderProps {
  activeLocation: LocationDto;
  cartCount: number;
  setCartCount: any;
  isMarketingBannerPage: boolean;
  users: MemberDto[]
}

enum HeaderMode {
  Visible,
  Hidden,
}

export default function AppHeader({ activeLocation, cartCount, setCartCount, isMarketingBannerPage, users }: AppHeaderProps) {
  const loc = useLocation();
  const userDropdownButton = useRef<HTMLDivElement>(null);

  const { location } = useContext(AppContext);
  const [member] = useRecoilState(memberState);
  const setMenuChat = useSetRecoilState(menuChatState);
  const [mode, setMode] = useState(HeaderMode.Visible);
  const needPro = !isProSubscription(member) && !member.diningRDIntegration;
  const limitedAccess = member.updateDrd || member.itemLibraryOnly || member.dataOnly || member.viewOnly;
  const showTemplatesNav = !limitedAccess || member.holidayTemplates;

  useEffect(() => {
    const { pathname } = loc;
    if (isLandingPath(pathname) || isDSAPath(pathname)) {
      setMode(HeaderMode.Hidden);
    } else {
      setMode(HeaderMode.Visible);
    }
  }, [loc]);

  useEffect(() => {
    // console.log("isMarketingBannerPage: " + isMarketingBannerPage);
  }, [isMarketingBannerPage]);

  useEffect(() => {
    if (!location || !location.internalId) return;

    OrdersApi.getDraftItemsCount(location.internalId!).then((resp) => {
      setCartCount(resp.data);
    });
  }, [member, location, setCartCount])

  function hideUserDropdown() {
    userDropdownButton.current?.click();
  }

  const account = (
    <div className={"act-sub-menu"} onClick={hideUserDropdown}>
      <header>
        <UserBadge name={member.name} />
        <section>
          <h5>{member.name}</h5>
          <h6>{member.email}</h6>
        </section>
      </header>
      <ul>
        <li>
          <NavLink to={env.dashboardPath + "/account/users"}>
            Account Settings
          </NavLink>
        </li>
        {(member.owner || member.role === "Admin") && <li className={needPro ? 'greyed-out' : ''}>
          <NavLink to={env.dashboardPath + "/account/team"} className={needPro ? 'pro-user' : ''}>
            My Team
            { needPro &&
            <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
          </NavLink>
        </li>}
        {(member.owner || member.role === "Admin") && <li className={needPro ? 'greyed-out' : ''}>
          <NavLink to={env.dashboardPath + "/account/locations"} className={needPro ? 'pro-user' : ''}>
            My Locations
            { needPro &&
            <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
          </NavLink>
        </li>}
        {(member.owner || member.role === "Admin" || (member as any).manageBrand) && <li>
          <NavLink to={env.dashboardPath + "/account/brand-management"}>
            My Brands
          </NavLink>
        </li>}
        <div className={"sep"} />
        <li>
          <a href={"/feature/menu-services.html"}>Menu &#38; Design Services</a>
        </li>
        <div className={"sep"} />
        <li>
          <a target="_blank" rel="noreferrer" href={HELP_CENTER_URL}>Help Center</a>
        </li>
        <li>
          <a href={"/menu/contact.do"}>Contact Us</a>
        </li>
        <div className={"sep"} />
        <li>
          <a href={"/menu/logoff.do"} onClick={logout}>Sign Out</a>
        </li>
      </ul>
    </div>
  );

  function onTemplatesClick() {
    window.location.href = member.holidayTemplates ? "/category/holiday-menus.html" : "/menu/browse.do";
  }

  const solutionsNav = <li>
    <Popover 
      overlayClassName="top-nav-popover" 
      content={<SolutionsMenu />} 
      placement="bottomLeft" 
      autoAdjustOverflow={true} 
      trigger={'hover'}
      showArrow={false}
    >
      <Button type={"link"} size={"large"} style={{width: '104px'}}>
        Solutions
      </Button>
    </Popover>
  </li>;

  return (
    mode === HeaderMode.Hidden ? null : (
      <>
        <header className={"main"} style={{top: !isMarketingBannerPage ? '0' : ''}}>
          <Logo activeLocation={activeLocation} />
          <nav className={`links-nav ${!member.id ? 'logged-out' : ''} ${limitedAccess ? 'view-only' : ''}`}>
            <ul>
              {member.id &&
              <li>
                <NavLink
                  className={
                    "ant-btn ant-btn-link ant-btn-lg" +
                    (window.location.pathname.includes("home") &&
                      !window.location.pathname.includes("account")
                      ? " active"
                      : "")
                  }
                  to={`${env.dashboardPath}/designs`}
                  style={{width: '69px'}}
                >
                  Home
                </NavLink>
              </li>}
              {member.id && showTemplatesNav &&
              <li>
                <Popover
                  overlayClassName="top-nav-popover" 
                  content={<TemplatesMenu />} 
                  placement="bottomLeft" 
                  autoAdjustOverflow={true} 
                  trigger={'hover'}
                  showArrow={false}
                >
                  <Button type={"link"} size={"large"} onClick={onTemplatesClick} style={{width: '102px'}}>
                    Templates
                  </Button>
                </Popover>
              </li>}
              {member.id && !limitedAccess && solutionsNav}
              {!member.id &&
              <li>
                <Popover 
                  overlayClassName="top-nav-popover" 
                  content={<ProductMenu />} 
                  placement="bottomLeft" 
                  autoAdjustOverflow={true} 
                  trigger={'hover'}
                  showArrow={false}
                >
                  <Button type={"link"} size={"large"} onClick={onTemplatesClick} style={{width: '86px', marginLeft: 24}}>
                    Product
                  </Button>
                </Popover>
              </li>}
              {!member.id &&
              <li>
                <Popover 
                  overlayClassName="top-nav-popover" 
                  content={<UseCasesMenu />}
                  placement="bottomLeft" 
                  autoAdjustOverflow={true} 
                  trigger={'hover'}
                  showArrow={false}
                >
                  <Button type={"link"} size={"large"} onClick={onTemplatesClick} style={{width: '139px'}}>
                    Who We Serve
                  </Button>
                </Popover>
              </li>}
              {!member.id &&
                <li>
                  <Popover
                    overlayClassName="top-nav-popover" 
                    content={
                      <div className={"templates-menu pricing-menu"}>
                        <ul className="pricing-links">
                          <li>
                            <a href={"/menu/pricing.do"}>
                              Pricing Plans
                            </a>
                          </li>
                          <li>
                            <a href={"/feature/menu-services.html"}>
                              Menu Services
                            </a>
                          </li>
                          <li>
                            <a href={"/feature/musthavemenus-vs-competitors.html"}>
                              MustHaveMenus vs Competitors
                            </a>
                          </li>
                        </ul>
                      </div>
                    }
                    placement="bottomLeft"
                    autoAdjustOverflow={true} 
                    trigger={'hover'}
                    showArrow={false}
                  >
                    <Button type={"link"} size={"large"} style={{width: '75px'}}>
                      Pricing
                    </Button>
                  </Popover>
                </li>
              }
              {!member.id &&
                <li>
                  <Popover
                    overlayClassName="top-nav-popover"
                    content={<ResourcesMenu />}
                    placement="bottomLeft"
                    autoAdjustOverflow={true}
                    trigger={'hover'}
                    showArrow={false}
                  >
                    <Button type={"link"} size={"large"} style={{width: '105px'}}>
                      Resources
                    </Button>
                  </Popover>
                </li>
              }
            </ul>
          </nav>

          <aside>
            {!limitedAccess && <Space>
              <SearchCategories />
            </Space>}

            {member.id && <nav>
              <ul>
                {hasFeature(member, MENU_CHAT) && <li>
                  <Tooltip placement="bottom" title={"Menu Assistant"}>
                    <Button
                      type={"link"}
                      className={"menu-chat"}
                      aria-label={"Menu Assistant"}
                      onClick={() => setMenuChat((chat) => ({ ...chat, open: true }))}
                    >
                      <MessageOutlined />
                    </Button>
                  </Tooltip>
                </li>}
                <li>
                  <Tooltip placement="bottom" title={"Settings"}>
                    <NavLink
                      className={"ant-btn ant-btn-link settings"}
                      to={env.dashboardPath + "/account/users"}
                    >
                      <GearIcon />
                    </NavLink>
                  </Tooltip>
                </li>
                {!limitedAccess && <li>
                  <Tooltip placement="bottom" title={"Print Cart"}>
                    <NavLink className={"ant-btn ant-btn-link cart"} to={env.dashboardPath + "/print/checkout"}>
                      <CartIcon />
                      {(cartCount > 0) && <span className={"cart-count"}>{cartCount}</span>}
                    </NavLink>
                  </Tooltip>
                </li>}
              </ul>
              <Dropdown overlay={account} trigger={["click"]} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
                <Button type={"link"} className={"bdg"} ref={userDropdownButton}>
                  <UserBadge name={member.name} />
                </Button>
              </Dropdown>
            </nav>}

            {!member.id && <nav>
              <Button className={"login-btn"} style={{ marginLeft: "10px" }}
                onClick={() => window.location.href = "/account/login"}>Log In</Button>
            </nav>}

            {!limitedAccess && <div>
              <Button className={`demo-btn ${!member.id ? 'logged-out' : ''}`} href={member.id ? '/feature/book-demo-auth.html' : '/feature/book-demo-hello.html'} target={"_blank"} type={"primary"} size={"large"}>
                Book an Intro
                <ArrowRightOutlined/>
              </Button>
            </div>}
          </aside>
        </header>

        <MobileAppHeader activeLocation={activeLocation} cartCount={cartCount} setCartCount={setCartCount} users={users} />
      </>
    )
  );
}
